import React, { useEffect, useState, useCallback } from 'react';
import { useApp } from '../../core/AppContext';

/**
 * ReviewQueueView — the human-approval surface for the autonomous governance queue.
 *
 * The orchestrator files PENDING proposals (add a challenger, advance a flip stage,
 * promote, roll back). A reviewer with the promote privilege approves or rejects here;
 * approving executes the underlying action (push / flip / promote) on the backend.
 *
 * Human approval is required in EVERY environment until an admin adds that environment
 * to AppConfig["approvals"]["auto_approve_envs"].
 */

const KIND_LABEL = {
  add_challenger: 'Add challenger',
  advance_stage: 'Advance flip stage',
  promote: 'Promote to primary',
  rollback: 'Roll back flip',
  pull: 'Remove from VM',
};

function Metric({ label, ch, inc, unit = '', betterLower = false }) {
  const better = (ch != null && inc != null)
    ? (betterLower ? ch < inc : ch > inc) : null;
  const col = better === true ? '#155724' : better === false ? '#b00020' : '#333';
  return (
    <tr>
      <td style={{ padding: '2px 8px', color: '#555' }}>{label}</td>
      <td style={{ padding: '2px 8px', textAlign: 'right', color: col, fontWeight: 'bold' }}>{ch ?? '—'}{unit}</td>
      <td style={{ padding: '2px 8px', textAlign: 'right' }}>{inc ?? '—'}{unit}</td>
    </tr>
  );
}

function ProposalCard({ p, canDecide, headers, onDecided }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ev = p.evidence || {};
  const cmp = ev.comparison || {};
  const cand = ev.candidate || {};

  const act = async (verb) => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`/api/proposals/${encodeURIComponent(p.ts_id)}/${verb}`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setErr((data && data.error) || `Could not ${verb}.`); setBusy(false); return; }
      onDecided(p.ts_id);
    } catch (e) {
      setErr('Network error.'); setBusy(false);
    }
  };

  return (
    <div style={{ border: '1px solid #e2e8f0', borderLeft: '4px solid #fd7e14', borderRadius: '8px', padding: '14px', marginBottom: '12px', background: '#fff' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
        <span style={{ background: '#fd7e14', color: '#fff', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold' }}>
          {KIND_LABEL[p.kind] || p.kind}
        </span>
        <span style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', border: '1px solid #ccc', borderRadius: '3px', padding: '1px 6px' }}>{p.environment}</span>
        <strong style={{ fontSize: '13px' }}>{p.vm}</strong>
        {p.from_stage != null && <span style={{ fontSize: '11px', color: '#b35c00' }}>{p.from_stage}% → {p.to_stage}%</span>}
      </div>

      <div style={{ fontSize: '13px', marginBottom: '6px' }}>
        <div><strong>Challenger:</strong> <span style={{ wordBreak: 'break-all' }}>{p.model_id}</span></div>
        <div><strong>Incumbent:</strong> {p.incumbent || '— (none behind VM yet)'}</div>
      </div>

      {(cand.blended != null || cand.cost != null) && (
        <div style={{ fontSize: '12px', color: '#444', marginBottom: '6px' }}>
          Funnel: blended <strong>{cand.blended ?? '—'}</strong>
          {cand.cost != null && <> · ${cand.cost}/1M</>}
          {cand.self_hosted && <span style={{ marginLeft: '6px', background: '#17a2b8', color: '#fff', borderRadius: '3px', padding: '1px 6px', fontSize: '10px' }}>self-hosted</span>}
        </div>
      )}

      {cmp.call && (
        <div style={{ fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '6px 8px', marginBottom: '8px', background: '#f8fafc' }}>
          <strong style={{ color: cmp.call === 'switch' ? '#155724' : '#555' }}>Verdict: {cmp.call}</strong>
          {cmp.rationale && <div style={{ color: '#444', marginTop: '2px' }}>{cmp.rationale}</div>}
        </div>
      )}

      <div style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>
        proposed by {p.created_by} · {p.created_ts}
      </div>

      {err && <div style={{ color: '#b00020', fontSize: '12px', marginBottom: '6px' }}>{err}</div>}

      {canDecide ? (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input placeholder="Optional note (recorded in the audit log)" value={note}
            onChange={e => setNote(e.target.value)}
            style={{ flex: 1, minWidth: '180px', padding: '6px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px' }} />
          <button disabled={busy} onClick={() => act('approve')}
            style={{ padding: '6px 14px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
            {busy ? '…' : 'Approve & execute'}
          </button>
          <button disabled={busy} onClick={() => act('reject')}
            style={{ padding: '6px 14px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
            Reject
          </button>
        </div>
      ) : (
        <div style={{ fontSize: '12px', color: '#888' }}>You need the promote privilege to approve or reject.</div>
      )}
    </div>
  );
}

export default function ReviewQueueView() {
  const { can, getAuthHeaders, role } = useApp();
  const canDecide = can('promote_other_to_echostar') || role === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/proposals?status=PENDING', { headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      setItems(data.proposals || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true); setMsg('');
    try {
      const res = await fetch('/api/orchestrator-run', {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ environment: 'sandbox', new_days: 1 }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMsg(`Tick complete — scanned ${data.scanned ?? '?'}, filed ${(data.proposals || []).length} proposal(s).`);
        load();
      } else {
        setMsg((data && data.error) || 'Orchestrator run failed.');
      }
    } catch (e) {
      setMsg('Network error.');
    } finally {
      setRunning(false);
    }
  };

  const removeLocally = (tsId) => setItems(xs => xs.filter(x => x.ts_id !== tsId));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#0f2027' }}>
            Model Governance — Approval Queue <span style={{ fontSize: '11px', backgroundColor: '#fd7e14', color: '#fff', padding: '3px 8px', borderRadius: '12px', verticalAlign: 'middle' }}>HUMAN-GATED</span>
          </h2>
          <p style={{ color: '#666', margin: '4px 0 0 0', fontSize: '13px' }}>
            The autonomous orchestrator proposes; you approve. Every environment requires a human until an admin enables auto-approve.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {(can('manage_config') || role === 'admin') && (
            <button onClick={runNow} disabled={running}
              style={{ padding: '8px 14px', background: '#0073bb', color: '#fff', border: 'none', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}>
              {running ? 'Running…' : '▶ Run orchestrator now'}
            </button>
          )}
          <button onClick={load} style={{ padding: '8px 14px', background: '#eef3f8', color: '#0073bb', border: '1px solid #b6d4fe', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}>Refresh</button>
        </div>
      </div>

      {msg && <p style={{ backgroundColor: '#eef3f8', padding: '10px', borderRadius: '4px', color: '#004085', fontSize: '13px' }}>{msg}</p>}

      {loading ? (
        <p style={{ color: '#666' }}>Loading pending proposals…</p>
      ) : items.length === 0 ? (
        <div style={{ color: '#888', background: '#fff', border: '1px dashed #cbd5e0', borderRadius: '8px', padding: '24px', textAlign: 'center' }}>
          No pending proposals. The orchestrator is watching. 🛰️
        </div>
      ) : (
        items.map(p => (
          <ProposalCard key={p.ts_id} p={p} canDecide={canDecide} headers={getAuthHeaders} onDecided={removeLocally} />
        ))
      )}
    </div>
  );
}
